/**
 * Estatisticas de tarefas (conclusoes por dia, pendencias).
 *
 * Complementa `features/focus/FocusStats.tsx`, que ja media TEMPO de foco. As
 * duas respondem perguntas diferentes e nao se substituem: dedicar tres horas a
 * um dia nao diz quantas tarefas sairam, e concluir doze coisas pequenas nao
 * diz quanto foco houve.
 *
 * "Em aberto" e "vencidas" NAO respeitam o periodo escolhido, de proposito: sao
 * um retrato do agora. Limita-las a janela faria "em aberto" significar
 * "abertas criadas nesta semana", que nao e o que a palavra diz.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type TaskStatsResponse } from '../../lib/api.js';

const DIA_CURTO = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];

/**
 * 'AAAA-MM-DD' -> rotulo curto.
 *
 * Sem passar por `new Date('2026-07-31')`, que seria interpretada como UTC e
 * poderia render o dia anterior no fuso do dispositivo. Mesmo cuidado do
 * `FocusStats`.
 */
function rotuloDoDia(date: string): string {
  const [year, month, day] = date.split('-').map(Number);
  if (!year || !month || !day) return date;

  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay();
  return `${DIA_CURTO[weekday]} ${String(day).padStart(2, '0')}`;
}

export function TaskStats() {
  const [range, setRange] = useState<'week' | 'month'>('week');

  const { data, isPending, isError } = useQuery<TaskStatsResponse>({
    queryKey: ['task-stats', range],
    queryFn: () => api.tasks.stats(range),
  });

  const stats = data?.stats;
  const doDia = stats?.byDay.find((d) => d.date === stats.today);
  const maior = Math.max(
    1,
    ...(stats?.byDay ?? []).map((d) => Math.max(d.criadas, d.concluidas)),
  );

  return (
    <section className="flex w-full max-w-lg flex-col gap-4">
      <header className="flex items-center justify-between">
        <h2 className="font-display text-md font-semibold">Tarefas</h2>

        <div className="flex gap-1" role="tablist" aria-label="Período das estatísticas">
          {(['week', 'month'] as const).map((item) => (
            <button
              key={item}
              type="button"
              role="tab"
              aria-selected={item === range}
              onClick={() => setRange(item)}
              className={[
                'min-h-touch rounded-sm px-3 text-sm transition-colors duration-150',
                item === range
                  ? 'bg-brand-blue-soft font-medium text-brand-blue'
                  : 'text-text-mut hover:bg-surface-2',
              ].join(' ')}
            >
              {item === 'week' ? 'Semana' : 'Mês'}
            </button>
          ))}
        </div>
      </header>

      {isPending ? <div className="h-24 animate-pulse rounded bg-surface-2" /> : null}

      {isError ? (
        <p className="rounded bg-surface p-4 text-sm text-overdue" role="alert">
          Não foi possível carregar as estatísticas.
        </p>
      ) : null}

      {stats ? (
        <>
          <div className="grid grid-cols-2 gap-2">
            <Cartao valor={doDia?.concluidas ?? 0} rotulo="concluídas hoje" />
            <Cartao valor={stats.totalConcluidas} rotulo="no período" />
            <Cartao valor={stats.abertas} rotulo="em aberto" />
            {/*
              Vermelho e reservado a vencido (convencao 10 do HANDOFF), e so
              acende quando ha algo: um zero em vermelho seria alarme falso.
            */}
            <Cartao valor={stats.vencidas} rotulo="vencidas" alerta={stats.vencidas > 0} />
          </div>

          {stats.byDay.length === 0 ? (
            <p className="rounded bg-surface p-6 text-center text-sm shadow-card">
              Nenhuma tarefa criada ou concluída no período.
            </p>
          ) : (
            <div className="flex flex-col gap-2 rounded bg-surface p-4 shadow-card">
              <div className="flex items-center justify-end gap-3 pb-1 text-xs text-text-mut">
                <span className="flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full bg-brand-blue" aria-hidden="true" />
                  concluídas
                </span>
                <span className="flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full bg-border" aria-hidden="true" />
                  criadas
                </span>
              </div>

              {stats.byDay.map((dia) => (
                <div key={dia.date} className="flex items-center gap-2 text-sm">
                  <span className="w-16 shrink-0 text-text-mut">{rotuloDoDia(dia.date)}</span>

                  <div className="flex flex-1 flex-col gap-0.5">
                    <Barra valor={dia.concluidas} maior={maior} cor="bg-brand-blue" />
                    <Barra valor={dia.criadas} maior={maior} cor="bg-border" />
                  </div>

                  <span className="w-14 shrink-0 text-right tabular-nums text-text-mut">
                    {dia.concluidas}/{dia.criadas}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      ) : null}
    </section>
  );
}

function Cartao({ valor, rotulo, alerta }: { valor: number; rotulo: string; alerta?: boolean }) {
  return (
    <div className="rounded bg-surface p-3 shadow-card">
      <p
        className={[
          'font-display text-xl font-semibold tabular-nums',
          alerta ? 'text-overdue' : '',
        ].join(' ')}
      >
        {valor}
      </p>
      <p className="text-xs text-text-mut">{rotulo}</p>
    </div>
  );
}

/**
 * Barras proporcionais ao MAIOR dia do periodo, e nao a uma meta fixa: sem meta
 * declarada, uma escala absoluta so diria ao usuario que ele esta abaixo de um
 * numero que ninguem combinou. Mesmo criterio do `FocusStats`.
 */
function Barra({ valor, maior, cor }: { valor: number; maior: number; cor: string }) {
  return (
    <div className="h-2 overflow-hidden rounded-sm bg-surface-2">
      <div className={`h-full rounded-sm ${cor}`} style={{ width: `${(valor / maior) * 100}%` }} />
    </div>
  );
}
